import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';
import { AppConfigService } from '../../services/app-config.service';
import { buildCropSeasonStyleIdLookup } from '../field-crop-style.util';
import {
    FieldBoundaryCollection,
    FieldBoundaryDecoderService,
} from './field-boundary-decoder.service';
import { FieldTile, FieldUnitType } from '../map.service';

export interface FieldBoundaryLoadCrop {
    id: string;
    dataScienceId?: number;
}

export interface FieldBoundaryLoadRequest {
    year: number;
    seasonAccessKey: string;
    seasonName: string;
    crops: FieldBoundaryLoadCrop[];
    tile: FieldTile;
    unitKeys: string[];
    cropAccessKeyFor: (crop: FieldBoundaryLoadCrop) => string;
    /** When false, skip fetching this crop/unit pair (plantation report gating). Default: always fetch. */
    shouldFetchPair?: (crop: FieldBoundaryLoadCrop, unitKey: string, unitType: FieldUnitType) => boolean;
}

@Injectable({ providedIn: 'root' })
export class FieldBoundaryLoadService {
    private readonly http = inject(HttpClient);
    private readonly appConfigService = inject(AppConfigService);
    private readonly fieldBoundaryDecoder = inject(FieldBoundaryDecoderService);

    private readonly cache = new Map<string, ArrayBuffer>();

    buildFieldPath(
        basePath: string,
        year: number,
        seasonAccessKey: string,
        cropAccessKey: string,
        unitType: string,
        unitAccessKey: string,
        zoom: number,
    ): string {
        return `${basePath}/crop-plantation/${year}/${seasonAccessKey}/${cropAccessKey}/${unitType}/${unitAccessKey}_z${zoom}.bin`;
    }

    buildFieldCacheKey(
        year: number,
        seasonAccessKey: string,
        cropAccessKey: string,
        unitType: string,
        unitAccessKey: string,
        zoom: number,
    ): string {
        return `${year}/${seasonAccessKey}/${cropAccessKey}/${unitType}/${unitAccessKey}_z${zoom}`;
    }

    loadMerged(request: FieldBoundaryLoadRequest): Observable<FieldBoundaryCollection> {
        const basePath = this.appConfigService.plantationPath?.replace(/\/+$/, '');
        const empty: FieldBoundaryCollection = { type: 'FeatureCollection', features: [] };
        if (!basePath || request.crops.length === 0 || request.unitKeys.length === 0) {
            return of(empty);
        }

        const { unitType, fileZoom } = request.tile;
        const shouldFetch =
            request.shouldFetchPair ??
            (() => true);

        const fetches: Observable<{ cacheKey: string; unitKey: string; buf: ArrayBuffer } | null>[] = [];

        for (const crop of request.crops) {
            const cropAccessKey = request.cropAccessKeyFor(crop);
            for (const unitKey of request.unitKeys) {
                if (!shouldFetch(crop, unitKey, unitType)) {
                    continue;
                }
                const cacheKey = this.buildFieldCacheKey(
                    request.year,
                    request.seasonAccessKey,
                    cropAccessKey,
                    unitType,
                    unitKey,
                    fileZoom,
                );
                const url = this.buildFieldPath(
                    basePath,
                    request.year,
                    request.seasonAccessKey,
                    cropAccessKey,
                    unitType,
                    unitKey,
                    fileZoom,
                );
                const cached = this.cache.get(cacheKey);
                const stream$ = cached ? of(cached) : this.http.get(url, { responseType: 'arraybuffer' });
                fetches.push(
                    stream$.pipe(
                        map((buf) => ({ cacheKey, unitKey, buf })),
                        catchError(() => of(null)),
                    ),
                );
            }
        }

        if (fetches.length === 0) {
            return of(empty);
        }

        return forkJoin(fetches).pipe(
            map((results) => {
                const collections: FieldBoundaryCollection[] = [];
                for (const item of results) {
                    if (!item) continue;
                    try {
                        this.cache.set(item.cacheKey, item.buf);
                        collections.push(this.fieldBoundaryDecoder.decodeFields(item.unitKey, item.buf));
                    } catch {
                        /* skip failed decode */
                    }
                }
                const merged =
                    collections.length > 0
                        ? this.fieldBoundaryDecoder.mergeFeatureCollections(collections)
                        : empty;
                return this.enrichWithSeasonCropStyleIds(merged, request);
            }),
            catchError(() => of(empty)),
        );
    }

    enrichWithSeasonCropStyleIds(
        collection: FieldBoundaryCollection,
        request: Pick<FieldBoundaryLoadRequest, 'seasonName' | 'crops'>,
    ): FieldBoundaryCollection {
        if (collection.features.length === 0 || request.crops.length === 0) {
            return collection;
        }

        const seasonName = request.seasonName?.toLowerCase() ?? '';
        const useWinter = seasonName.includes('winter');
        const styleIdByCropId = buildCropSeasonStyleIdLookup(
            request.crops.map((c) => ({
                id: c.id,
                dataScienceId: c.dataScienceId,
            })),
        );

        for (const feature of collection.features) {
            const payloadCropId = feature.properties.crop_id?.trim().toLowerCase();
            if (!payloadCropId) continue;

            const styleId = styleIdByCropId.get(payloadCropId);
            if (styleId == null) continue;

            if (useWinter) {
                feature.properties.winter_crop_id = styleId;
            } else {
                feature.properties.summer_crop_id = styleId;
            }
        }

        return collection;
    }
}
